import React, { useState } from 'react';

import Observer from '@researchgate/react-intersection-observer';

import { AnimatedImages } from '@ui/moleculs/atoms/AnimatedImage';
import { AnimatedLinks } from '@ui/moleculs/atoms/AnimatedLinks';

import { ReactComponent as Menu1 } from '@assets/images/section-4/menu-1.svg';
import { ReactComponent as Menu2 } from '@assets/images/section-4/menu-2.svg';

import { ReactComponent as Menu1Mob } from '@assets/images/section-4/menu-1-mob.svg';
import { ReactComponent as Menu2Mob } from '@assets/images/section-4/menu-2-mob.svg';

import { ReactComponent as MenuNumber } from '@assets/images/section-4/menu-number.svg';

import Image1 from '@assets/images/section-4/item-1.webp';
import Image2 from '@assets/images/section-4/item-2.webp';
import Image3 from '@assets/images/section-4/item-3.webp';
import Image4 from '@assets/images/section-4/item-4.webp';
import Image5 from '@assets/images/section-4/item-5.webp';

import Image1Safari14 from '@assets/images/section-4/item-1-safari-14.png';
import Image2Safari14 from '@assets/images/section-4/item-2-safari-14.png';
import Image3Safari14 from '@assets/images/section-4/item-3-safari-14.png';
import Image4Safari14 from '@assets/images/section-4/item-4-safari-14.png';
import Image5Safari14 from '@assets/images/section-4/item-5-safari-14.png';

import { ReactComponent as SliderBGSafari14 } from '@assets/images/animated-links/slider-bg-safari-14.svg';

import { KeenSlider } from '@ui/moleculs/atoms/KeenSlider';

import styles from './index.module.css'

type PageProps = {
  scrollDir?: string;
  isDesktopOrLaptop?: any;
  browser: any;
};

const data = [
  {
    id: 1,
    image: Image1,
    imageSafari: Image1Safari14,
    title: 'ПОЛИТИЧЕСКОЕ ДАВЛЕНИЕ',
    body: 'Изначально против СП-2 выступил целый ряд стран Европы. <span class="text-blue-default font-bold">Наиболее активными критиками проекта предсказуемо стали Польша и Украина.</span> Польские регуляторы неоднократно предъявляли судебные иски к «Газпрому» о нарушении антимонопольного законодательства, тогда как киевские власти всячески ходатайствовали об ужесточении санкционного режима в отношении СП-2.',
    hover: `Канцлер Германии Ангела Меркель и премьер-министр Польши Матеуш Моравецкий на встрече 11 сентября 2021 года в Варшаве, Польша (Фото: Omar Marques/Getty Images).`
  },
  {
    id: 2,
    image: Image2,
    imageSafari: Image2Safari14,
    title: 'САНКЦИОННОЕ ДАВЛЕНИЕ',
    body: 'С принятием 21 декабря 2019 года нового оборонного бюджета, американское правительство фактически наложило санкции на СП-2 и компании, участвующие в его строительстве. <span class="text-blue-default font-bold">В мае 2021 года эти санкции были полностью отменены, однако до сих пор сохраняется вероятность их повторного введения, в случае ухудшения внешнеполитической ситуации.</span>',
    hover: 'Госсекретарь США Энтони Блинкен выступает на пресс-конференции в Брюсселе, Бельгия, в среду, 24 марта 2021 года (Фото: Olivier Matthys/Pool/Bloomberg via Getty Images).'
  },
  {
    id: 3,
    image: Image3,
    imageSafari: Image3Safari14,
    title: 'ЮРИДИЧЕСКОЕ ДАВЛЕНИЕ',
    body: 'В мае 2019-го была принята Газовая директива ЕС, под нормы которой подпадает «Северный поток-2». Согласно этому документу, владелец газопровода не имеет права единолично использовать более 50% его мощностей. <span class="text-blue-default font-bold">Таким образом, для использования СП-2 в полном объёме, «Газпрому» необходимо добиться того, чтобы оператор газопровода — компания Nord Stream 2 AG — была признана независимой.</span>',
    hover: '12 декабря 2019 года, Мекленбург-Передняя Померания, Засниц: трубы для газопровода «Северный поток-2» готовятся к погрузке на судно в порту Мукран (Фото: Stefan Sauer/picture alliance via Getty Images).'
  },
  {
    id: 4,
    image: Image4,
    imageSafari: Image4Safari14,
    title: 'ЭКОЛОГИЧЕСКОЕ ДАВЛЕНИЕ',
    body: '<span class="text-blue-default font-bold">Значительная часть европейских политических элит, продвигающих «зелёную» повестку, посчитала СП-2 угрозой для скорейшего перехода Евросоюза на возобновляемые источники энергии.</span> В частности, немецкие «зелёные», которые набрали в последнее время значительный политический вес, в первой половине 2021 года открыто выступила против СП-2: в партии заявили, что газ, поставляемый по этому трубопроводу, будет считаться «грязным» топливом, подлежащим налогообложению в рамках нового углеродного регулирования ЕС.',
    hover: `Протестующие против продолжения строительства газопровода «Северный поток-2» (Фото: Jens Büttner/picture alliance via Getty Images).`
  },
];

export const Page4: React.FC<PageProps> = ({ ...props }) => {
  const [isVisible, setVisible] = useState([false]);
  const { scrollDir, isDesktopOrLaptop, browser } = props;

  const handleChange = (event: any | never, index: number) => {
    const currActive = isVisible[index];
    const updateArr = [ ...isVisible ];
    updateArr[index] = event.isIntersecting || (currActive && scrollDir === 'down')
    setVisible(updateArr);
  };

  const browserName = browser.name.toLowerCase();
  const browserVersion = browser.version.split('.')[0];

  return (
    <Observer
      onChange={event => handleChange(event, 0)}
      root={null}
      rootMargin="0% 0% -20%"
    >
      <article
        id="problem"
        className={`
          ${isVisible[0] ? styles.active : ``}
          ${styles.section}
          relative
          max-w-1100
          w-full
          mx-auto
          text-white

          mb-38
          lg:mb-45
        `}
      >
        <header
          className={`
            flex
            lg:inline-flex
            justify-between
            lg:justify-start
            relative
            -mt-8
            lg:mt-0
            pt-8
            lg:pt-0
            pb-14
            lg:pb-18
            pl-8
            mr-4
            lg:pl-0
            lg:mr-0

            overflow-hidden
            lg:overflow-visible
          `}
        >
          <h1
            className={`
              inline-flex
              flex-col
              items-stretch
              lg:items-start
              relative
              font-bold
              min-w-full
              lg:min-w-0

              text-4.78xl
              lg:text-6.5xl
            `}
          >
            <span
              className={`
                relative
                transform
                duration-500
                transition-all
              `}
            >
              {isDesktopOrLaptop ?
                <Menu1
                  className={`
                    ${styles.menubg1}
                    absolute
                    transform
                    duration-500
                    delay-500
                    transition-all
                    -ml-10
                    max-w-full
                    lg:max-w-none
                    h-auto
                  `}
                />
                :
                <Menu1Mob
                  className={`
                    ${styles.menubg1}
                    absolute
                    transform
                    duration-500
                    delay-500
                    transition-all
                    -mt-2
                    -ml-5
                    max-w-full
                    lg:max-w-none
                    h-auto
                  `}
                />
              }
              <span className={`relative`}>Препоны</span>
            </span>
            <span
              className={`
                relative
                transform
                duration-500
                transition-all
              `}
            >
              {isDesktopOrLaptop ?
                <Menu2
                  className={`
                    ${styles.menubg2}
                    absolute
                    transform
                    duration-500
                    transition-all
                    mt-3
                    ml-16
                    max-w-full
                    lg:max-w-none
                    h-auto
                  `}
                />
                :
                <Menu2Mob
                  className={`
                    ${styles.menubg2}
                    absolute
                    transform
                    duration-500
                    transition-all
                    mt-5
                    ml-11
                    max-w-full
                    lg:max-w-none
                    h-auto
                  `}
                />
              }
              <span className={`relative`}>и проблемы</span>
            </span>
            <span
              className={`
                relative
                transform
                duration-500
                delay-700
                transition-all
              `}
            >
              <span className={`relative`}>реализации</span>
            </span>
          </h1>
          <h2 className={`
            absolute
            top-0
            lg:-top-8
            right-0
            lg:-right-20
            z-2
          `}>
            <MenuNumber
              width={isDesktopOrLaptop ? 208 : 162}
              height={isDesktopOrLaptop ? 230 : 182}
              className={`
                ${styles.number1}
                relative
                fill-current
              `}
            />
            <MenuNumber
              width={isDesktopOrLaptop ? 208 : 162}
              height={isDesktopOrLaptop ? 230 : 182}
              className={`
                ${styles.number2}
                absolute
                top-0
                right-0
                fill-current
              `}
            />
          </h2>
        </header>

        <Observer
          onChange={event => handleChange(event, 1)}
          root={null}
          rootMargin={isDesktopOrLaptop ? `0% 0% -20%` : `0% 0% 50%`}
        >
          <div
            className={`
              ${isVisible[1]? `animate-opacity` : ``}

              transform
              opacity-0
              translate-y-full

              mb-6vw
            `}>
            <KeenSlider isDesktopOrLaptop={isDesktopOrLaptop}>
              <React.Fragment>
                {data.map((item, index) => 
                  <div
                    key={index}
                    className={`
                      keen-slider__slide
                      number-slide1
                      max-w-890
                      mx-auto
                      mb-2
                      lg:mb-15vw
                    `}>
                    <div className={`flex flex-col lg:flex-row w-full`}>
                      <div
                        className={`
                          relative
                          flex-1
                          text-xxs
                          lg:text-sm
                          font-medium
                          leading-6
                          text-white
                          order-2
                          lg:order-1
                          mx-8
                          lg:mx-0
                        `}
                      >
                        <h3
                          className={`
                            uppercase
                            font-bold
                            text-xlxl
                            mb-2
                            lg:mb-3.5vw
                            lg:pl-24
                          `}
                        >
                          {item.title}
                        </h3>
                        <span
                          className={`
                            inline-block
                            relative
                            text-base
                            lg:pl-24
                            indent-sm
                            lg:mr-11.5vw
                          `}
                          dangerouslySetInnerHTML={{ __html: item.body }}
                        />
                      </div>
                      <div
                        className={`
                          flex-1
                          order-1
                          lg:order-2
                          mb-4
                          lg:mb-0
                          flex
                          lg:block
                          justify-center
                        `}
                      >
                        <div
                          className={`
                            relative

                            overflow-hidden
                            lg:overflow-visible
                            ml-8
                            mr-8
                            lg:ml-11.5vw
                            lg:mr-0
                          `}
                        >
                          <AnimatedImages
                            id={`page-4-image-${index}`}
                            href={(browserName === 'safari' && browserVersion < 15) ? item.imageSafari : item.image}
                            body={item.hover}
                            orientation={`inverse`}
                            isDesktopOrLaptop={isDesktopOrLaptop}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </React.Fragment>
            </KeenSlider>
          </div>
        </Observer>

        <Observer
          onChange={event => handleChange(event, 2)}
          root={null}
          rootMargin={isDesktopOrLaptop ? `0% 0% 0%` : `0% 0% 45%`}
        >
          <div
            className={`
              ${isVisible[2]? `animate-opacity` : ``}

              transform
              opacity-0
              translate-y-full
              relative
              z-3
            `}
          >
            <AnimatedLinks
              id={`page4-link1`}
              className={`transform transition-transform duration-1000 lg:hover:scale-110`}
              title={`Последствием отказа от СП-2 будет конец ФРГ как промышленной державы`}
              body={`Председатель «Альтернативы для Германии» Йорг Мойтен — о будущем «Северного потока – 2» после смены канцлера и о том, как это повлияет на отношения Москвы и Берлина`}
              link={`https://iz.ru/1206999/elnar-bainazarov/posledstviem-otkaza-ot-sp-2-budet-konetc-frg-kak-promyshlennoi-derzhavy`}
              href={(browserName === 'safari' && browserVersion < 15) ? Image5Safari14 : Image5}
              size={`lg`}
              isDesktopOrLaptop={isDesktopOrLaptop}
            />
          </div>
        </Observer>

      </article>
    </Observer>
  );
}